import React, { useState } from 'react';
import { Provider } from '../types';
import {
  UserPlus,
  X,
  AlertCircle,
  ShieldCheck,
  Crown,
  UserCheck,
  Check,
  Info
} from 'lucide-react';
import { PulseCoreIcon } from './PulseCoreIcon';
import { getRoleEntitlementStyles } from '../utils/roleAccent';

interface AddAppUserModalProps {
  onClose: () => void;
  onCreated: (newUser: any) => void;
  providers: Provider[];
  existingUsers: any[];
}

export const AddAppUserModal: React.FC<AddAppUserModalProps> = ({
  onClose,
  onCreated,
  providers,
  existingUsers
}) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [assignedRoles, setAssignedRoles] = useState<string[]>(['ASSISTANT']);
  const [selectedProviderId, setSelectedProviderId] = useState('');
  const [active, setActive] = useState(true);
  const [roleSuggestionNotice, setRoleSuggestionNotice] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // When a clinical provider is selected, suggest (but do not force) clinical role
  const handleProviderSelect = (provId: string) => {
    setSelectedProviderId(provId);
    setRoleSuggestionNotice(null);

    if (!provId) return;

    const p = providers.find((prov) => prov.id === provId);
    if (!p) return;

    const role = (p.role || '').toUpperCase();
    const title = (p.title || '').toLowerCase();

    if (role === 'SLP' || role === 'SUPERVISOR' || title.includes('pathologist')) {
      if (!assignedRoles.includes('SUPERVISOR')) {
        setAssignedRoles((prev) => Array.from(new Set([...prev.filter((r) => r !== 'ASSISTANT'), 'SUPERVISOR'])));
        setRoleSuggestionNotice('Suggested role "SUPERVISOR" selected based on provider clinical credential (SLP). You may adjust if needed.');
      }
    } else if (role === 'SLPA' || role === 'ASSISTANT' || title.includes('assistant')) {
      if (!assignedRoles.includes('ASSISTANT')) {
        setAssignedRoles((prev) => Array.from(new Set([...prev.filter((r) => r !== 'SUPERVISOR'), 'ASSISTANT'])));
        setRoleSuggestionNotice('Suggested role "ASSISTANT" selected based on provider clinical credential (SLPA). You may adjust if needed.');
      }
    }
  };

  const handleToggleRole = (roleKey: string) => {
    setRoleSuggestionNotice(null);
    if (assignedRoles.includes(roleKey)) {
      if (assignedRoles.length === 1) {
        setError('App User must have at least one assigned application role.');
        return;
      }
      setAssignedRoles((prev) => prev.filter((r) => r !== roleKey));
    } else {
      setAssignedRoles((prev) => [...prev, roleKey]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const cleanName = name.trim();
    if (!cleanName) {
      setError('Full Name is required.');
      return;
    }

    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail) {
      setError('Google Workspace Login Email is required.');
      return;
    }

    if (!cleanEmail.endsWith('@allegianttherapy.com')) {
      setError('Google Workspace Login Email must belong to the @allegianttherapy.com domain.');
      return;
    }

    if (assignedRoles.length === 0) {
      setError('At least one application role entitlement must be selected.');
      return;
    }

    try {
      setSaving(true);
      const res = await fetch('/api/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: cleanName,
          email: cleanEmail,
          assignedRoles,
          providerId: selectedProviderId || null,
          active
        })
      });

      if (!res.ok) {
        const errJson = await res.json().catch(() => ({}));
        throw new Error(errJson.error || `Server returned error status ${res.status}`);
      }

      const data = await res.json();
      onCreated(data.user);
      onClose();
    } catch (err: any) {
      console.error('[AddAppUserModal] Creation error:', err);
      setError(err.message || 'Failed to provision application user.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <div className="glass-panel max-w-lg w-full rounded-2xl border border-slate-800 p-6 space-y-5 my-8">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-800 pb-3">
          <div className="flex items-center space-x-2.5">
            <div className="p-2 rounded-xl bg-sky-500/10 border border-sky-500/20 text-sky-400">
              <UserPlus className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-white">Add App User</h2>
              <p className="text-[11px] text-slate-400">Provision Google Workspace Login & Role Entitlements</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-slate-400 hover:text-white rounded-lg hover:bg-white/5 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="p-3 bg-rose-500/10 border border-rose-500/20 text-rose-300 rounded-lg text-xs flex items-center space-x-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 text-xs">
          {/* Full Name */}
          <div>
            <label className="block text-slate-300 font-semibold mb-1">
              Full Legal / Staff Name *
            </label>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Maria Gonzalez"
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-slate-100 focus:outline-none focus:border-sky-500"
            />
          </div>

          {/* Google Workspace Login Email */}
          <div>
            <label className="block text-slate-300 font-semibold mb-1">
              Google Workspace Login Email *
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="e.g. mgonzalez@allegianttherapy.com"
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-slate-100 focus:outline-none focus:border-sky-500"
            />
            <p className="text-[10px] text-slate-400 mt-1.5 leading-relaxed">
              Used only for Bloom sign-in. This does not change the provider's clinical contact or eSignature email.
            </p>
          </div>

          {/* Optional Linked Clinical Provider */}
          <div className="p-3 bg-slate-900/60 border border-slate-800 rounded-xl space-y-1.5">
            <label className="block text-slate-300 font-semibold flex items-center justify-between">
              <span>Optional Linked Clinical Provider</span>
              <span className="text-[10px] text-slate-500 font-normal">Explicit Selection</span>
            </label>
            <select
              value={selectedProviderId}
              onChange={(e) => handleProviderSelect(e.target.value)}
              className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-slate-100 focus:outline-none focus:border-sky-500 text-xs"
            >
              <option value="">Operational Staff / No Clinical Provider</option>
              {providers.map((p) => {
                const linkedUser = existingUsers.find(
                  (u) => u.id === p.user_id || u.providerId === p.id
                );
                const isAlreadyLinked = Boolean(linkedUser);

                return (
                  <option
                    key={p.id}
                    value={p.id}
                    disabled={isAlreadyLinked}
                  >
                    {p.name} ({p.role}) {isAlreadyLinked ? `— [Already Linked to ${linkedUser?.name || 'Staff'}]` : ''}
                  </option>
                );
              })}
            </select>
            <p className="text-[10px] text-slate-400 leading-relaxed">
              Associates this login identity with an active clinician record for caseload assignment and documentation. Never matched automatically by email.
            </p>
            {roleSuggestionNotice && (
              <div className="p-2 mt-1 rounded bg-sky-950/30 border border-sky-800/40 text-[10.5px] text-sky-300 flex items-start space-x-1.5">
                <Info className="w-3.5 h-3.5 text-sky-400 shrink-0 mt-0.5" />
                <span>{roleSuggestionNotice}</span>
              </div>
            )}
          </div>

          {/* Role Entitlements */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-slate-300 font-semibold">
                Role Entitlements * (Select one or more)
              </label>
              <span className="text-[10px] text-slate-500">OWNER Authoritative</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {(['OWNER', 'REFERRAL_COORDINATOR', 'SUPERVISOR', 'ASSISTANT'] as const).map((rKey) => {
                const isSelected = assignedRoles.includes(rKey);
                const theme = getRoleEntitlementStyles(rKey);

                let icon = null;
                if (rKey === 'OWNER') {
                  icon = <PulseCoreIcon className={`w-3.5 h-3.5 mr-1.5 shrink-0 ${isSelected ? theme.activeIcon : theme.inactiveIcon}`} />;
                } else if (rKey === 'REFERRAL_COORDINATOR') {
                  icon = <Crown className={`w-3.5 h-3.5 mr-1.5 shrink-0 ${isSelected ? theme.activeIcon : theme.inactiveIcon}`} />;
                } else if (rKey === 'SUPERVISOR') {
                  icon = <ShieldCheck className={`w-3.5 h-3.5 mr-1.5 shrink-0 ${isSelected ? theme.activeIcon : theme.inactiveIcon}`} />;
                } else {
                  icon = <UserCheck className={`w-3.5 h-3.5 mr-1.5 shrink-0 ${isSelected ? theme.activeIcon : theme.inactiveIcon}`} />;
                }

                return (
                  <button
                    key={rKey}
                    type="button"
                    onClick={() => handleToggleRole(rKey)}
                    className={`inline-flex items-center justify-between px-3 py-2 rounded-lg text-xs font-semibold border transition-all cursor-pointer ${
                      isSelected ? theme.activeContainer : theme.inactiveContainer
                    } hover:scale-[1.02] active:scale-95`}
                  >
                    <div className="flex items-center">
                      {icon}
                      <span>{rKey.replace('_', ' ')}</span>
                    </div>
                    {isSelected && <Check className={`w-3.5 h-3.5 ${theme.activeCheck}`} />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Active Access Toggle */}
          <div className="p-3 bg-slate-900/60 border border-slate-800 rounded-xl flex items-center justify-between">
            <div>
              <span className="font-semibold text-slate-200 block text-xs">Active Application Access</span>
              <span className="text-[10px] text-slate-400 block mt-0.5">
                Default ON. Deactivated accounts are blocked at the Google login gate.
              </span>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-9 h-5 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-500"></div>
            </label>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-2 pt-3 border-t border-slate-800">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-xs font-semibold transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2 bg-gradient-to-r from-sky-600 to-indigo-600 hover:from-sky-500 hover:to-indigo-500 text-white rounded-lg text-xs font-semibold shadow-md shadow-sky-900/30 transition-all hover:scale-[1.02] active:scale-95 disabled:opacity-50 flex items-center space-x-1.5"
            >
              {saving ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>Provisioning...</span>
                </>
              ) : (
                <>
                  <UserPlus className="w-3.5 h-3.5" />
                  <span>Create App User</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
